import _ from 'lodash';
import dataModel from '../models/DataModel';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import { RecordStates, AnalyticsKeys } from '../constants/ReduxConstants.js';

import analyticsService from '../services/analyticsService';

/**
 * REDUX ACTIONS
 */
function findAnalyticsObject(objectKey) {
  const stateTree = dataModel.stateTree;
  const analyticsObject = stateTree.analytics[objectKey];
  return _.cloneDeep(analyticsObject);
}

function setAnalyticsObject(objectKey, newObject) {
  const newObjectValue = _.cloneDeep(newObject);
  dataModel.store.dispatch({
    type: 'SET_ANALYTICS_OBJECT',
    objectKey,
    newObjectValue,
  });
}

function clearState(clearOptions) {
  if (clearOptions.clearAll || clearOptions.newUser) {
    dataModel.store.dispatch({ type: 'RESET_ANALYTICS' });
  }
}

/** 
 * RECORD STATE FUNCTIONS
 */
function markTaskAnalyticsNeedsSync() {
  markAnalyticsObjectNeedsSync(AnalyticsKeys.TASK_COUNTS);
  markAnalyticsObjectNeedsSync(AnalyticsKeys.TASK_METRICS);
}

function markAnalyticsObjectNeedsSync(objectKey) {
  let analyticsObject = findAnalyticsObject(objectKey);
  if (analyticsObject) {
    analyticsObject._recordState = RecordStates.NEEDS_SYNC;
    setAnalyticsObject(objectKey, analyticsObject);
  }    
}

function getTaskCountsFromStore(user_id, options) {
  let tasksCounts = findAnalyticsObject(AnalyticsKeys.TASK_COUNTS);
  return tasksCounts;
}

/**
 * DATA REQUEST FUNCTIONS
 */
function getTaskCounts(user_id, options) {
  let tasksCount = findAnalyticsObject(AnalyticsKeys.TASK_COUNTS);
  if (dataModel.isOffline) {
    let results = manuallyCountTasks(user_id);
    results._recordState = RecordStates.NEEDS_SYNC;
    // only store if values differ (lest we trigger an infinite loop)...
    if (!_.isEqual(tasksCount, results)) {
      setAnalyticsObject(AnalyticsKeys.TASK_COUNTS, results);
    } else {
      results = tasksCount;
    }
    return Promise.resolve(results);
  }
  if (!tasksCount || tasksCount._recordState == RecordStates.NEEDS_SYNC || (options && options.force)) { 
    return analyticsService.countMyTasks({ id: user_id }).then((results) => {
      if (results.error && !results.offline) {
        // throw Object.assign({message: 'Error retrieving task counts', error: results.error})
      }
      else if (results.offline) {
        results = manuallyCountTasks(user_id);
  
        setAnalyticsObject(AnalyticsKeys.TASK_COUNTS, results);
      }
      else {
        results._recordState = RecordStates.SYNCED;
        setAnalyticsObject(AnalyticsKeys.TASK_COUNTS, results);
      }
      return Promise.resolve(results);
    })
      .catch((error) => {
        // throw Object.assign({message: 'Error retrieving task counts', error})
      });
  }
  else {
    return Promise.resolve(tasksCount);
  }
}

function getTaskMetrics(options) {
  let taskMetrics = findAnalyticsObject(AnalyticsKeys.TASK_METRICS);
  if (dataModel.isOffline) {
    // TODO: enhancement to manually calculate as with task counts?
    markAnalyticsObjectNeedsSync(AnalyticsKeys.TASK_METRICS);
    return Promise.resolve(taskMetrics);
  }
  if (!taskMetrics || taskMetrics._recordState == RecordStates.NEEDS_SYNC || (options && options.force)) {
    return analyticsService.getTaskMetrics().then((results) => {
      console.log(results);
      if (results.error && !results.offline) {
        throw Object.assign({ message: 'Error retrieving task metrics', error: results.error });
      }
      else if (results.offline) {
        markAnalyticsObjectNeedsSync(AnalyticsKeys.TASK_METRICS);
        return taskMetrics;
      }
      else {
        results._recordState = RecordStates.SYNCED;
        setAnalyticsObject(AnalyticsKeys.TASK_METRICS, results);
      }
      return results;
    })
      .catch((error) => {
        throw Object.assign({ message: 'Error retrieving task metrics', error });
      });
  }
  else {
    return Promise.resolve(taskMetrics);
  }
}

/** 
 * OFFLINE FUNCTIONS 
 */
function manuallyCountTasks(user_id, favoritedOnly = true) {
  let results = { not_started_count: 0, in_progress_count: 0, in_review_count: 0, all_count: 0 };
  
  for (const task of Object.values(dataModel.stateTree.new_tasks)) {
    if (favoritedOnly && task.project_id) {
      let project = StoreItemProcessing.findItem('project', task.project_id);
      if (project && !project.is_favorite) {
        // ignore task belonging to unfavorited project
        continue;
      }
    }
    if (task.user_task_assignments) {
      let countTask = false;
      for (let uta_id of task.user_task_assignments) {
        let uta = StoreItemProcessing.findItem('user_task_assignment', uta_id);
        if (uta) {
          uta = StoreItemProcessing.denormalizeItem('user_task_assignment', uta);
          if (uta.user_id == user_id) {
            countTask = true;
            break;
          }
        }
      }
      if (countTask) {
        switch (task.status) {
        case 'not_started':
          results.not_started_count++;
          break;
        case 'in_progress':
          results.in_progress_count++;
          break;
        case 'pending_review':
        case 'pending_approval':
          results.in_review_count++;
          break;
        default:
          break;
        }
        results.all_count++;
      }
    }
  }
  return results;
}

export default {
  findAnalyticsObject,
  clearState,
  markTaskAnalyticsNeedsSync,
  getTaskCounts,
  getTaskMetrics,
  getTaskCountsFromStore,
};



// WEBPACK FOOTER //
// ./src/dao/AnalyticsDAO.js